"use client";

import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ConsultationFaq() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const faqs = [{
    value: "item-1",
    question: "How does a revenue audit improve my hotel's performance?",
    answer: "A revenue audit identifies hidden inefficiencies in your current OTA strategy, pricing models, and tech stack. By analyzing your data, we uncover opportunities to increase RevPAR and optimize your distribution channels for maximum profitability."
  }, {
    value: "item-2",
    question: "What is the typical timeline for seeing revenue growth?",
    answer: "While immediate adjustments to rate parity and OTA listings can yield quick wins, sustainable revenue growth typically stabilizes within 3 to 6 months as we refine your dynamic pricing and market positioning."
  }, {
    value: "item-3",
    question: "Do you work with independent hotels or chains?",
    answer: "We specialize in both. Whether you are an independent boutique hotel or a multi-property resort chain, our strategies are customized to your specific market, property size, and operational goals."
  }, {
    value: "item-4",
    question: "What is your approach to OTA management?",
    answer: "We focus on high-conversion listing optimization, competitive rate management, and strategic content updates. Our goal is to improve your visibility on platforms like Booking.com and Expedia while driving more direct bookings."
  }, {
    value: "item-5",
    question: "Can you help with hotel technology implementation?",
    answer: "Yes. We provide expert guidance on selecting and implementing the right Property Management Systems (PMS), Channel Managers, and Revenue Management Systems (RMS) to streamline your operations."
  }, {
    value: "item-6",
    question: "Is there a long-term contract required?",
    answer: "We offer flexible engagement models, ranging from one-time revenue audits to ongoing monthly consulting retainers. We believe in proving our value through consistent, data-driven results."
  }, {
    value: "item-7",
    question: "How do you handle rate parity issues?",
    answer: "We monitor your rates across all channels in real-time. When discrepancies are found, we provide actionable steps to correct them, ensuring your direct booking channel remains the most attractive option for guests."
  }, {
    value: "item-8",
    question: "How do I get started with a consultation?",
    answer: "Simply click the 'Book a Consultation' button on our homepage or contact us directly via email. We will schedule a brief discovery call to understand your property's unique challenges and revenue potential."
  }];
  return <section data-section-id="3156" id="productcomparisonfaq" className="bg-background text-foreground py-20 md:py-[120px]">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div ref={leftRef} className={`lg:col-span-4 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              <p className="font-default text-xs tracking-[0.25em] uppercase text-secondary mb-6">
                Frequently Asked
              </p>
              <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-6">
                Revenue <span className="italic font-light">Insights.</span>
              </h2>
              <p className="font-default text-base leading-relaxed text-muted-foreground mb-8 max-w-md">
                Common questions from hotel owners and managers. If you have specific concerns about your property, we are here to help.
              </p>
              <Link to="/ContactPage" className="inline-block">
                <Button variant="ghost" className="group h-auto px-0 py-2 font-default text-sm tracking-wide text-foreground hover:bg-transparent hover:text-secondary">
                  <Mail className="size-4 stroke-[1.25] mr-2" />
                  <span>Contact our team</span>
                  <span className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">
                    →
                  </span>
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-8 transition-all duration-1000 delay-150 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="single" collapsible className="w-full border-t border-border">
              {faqs.map((faq, i) => <AccordionItem data-index={i} key={faq.value} value={faq.value} className="border-b border-border last:border-b-0">
                  <AccordionTrigger className="group flex w-full items-center justify-between gap-6 py-8 text-left hover:no-underline [&>svg]:hidden">
                    <span className="font-default font-semibold text-xl md:text-[1.4rem] leading-snug text-foreground transition-colors duration-300 group-hover:text-secondary">
                      {faq.question}
                    </span>
                    <span className="relative flex size-8 shrink-0 items-center justify-center text-secondary">
                      <span className="absolute h-px w-4 bg-current" />
                      <span className="absolute h-4 w-px bg-current transition-transform duration-500 ease-out group-data-[state=open]:rotate-90 group-data-[state=open]:scale-0" />
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12">
                    <p className="font-default text-base leading-[1.7] text-muted-foreground">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}